import { ActivatedRoute, Params, Router } from '@angular/router';

export interface ProfilePaging {
    page: number;
    pageSize: number;
}

interface ResolvedProfilePaging extends ProfilePaging {
    queryParams: { page: number | null; pageSize: number | null };
}

export function resolveProfilePaging(
    params: Params | ProfilePaging,
    defaultPageSize: number,
    totalCount?: number,
): ResolvedProfilePaging {
    function positiveInteger(value: unknown, fallback: number): number {
        if (
            typeof value !== 'number' &&
            (typeof value !== 'string' || !/^\d+$/.test(value))
        ) {
            return fallback;
        }
        const parsed: number = Number(value);
        return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : fallback;
    }
    const pageSize: number = positiveInteger(params.pageSize, defaultPageSize);
    const requestedPage: number = positiveInteger(params.page, 1);
    const page: number =
        totalCount === undefined
            ? requestedPage
            : Math.min(
                  requestedPage,
                  Math.max(1, Math.ceil(totalCount / pageSize)),
              );

    return {
        page,
        pageSize,
        queryParams: {
            page: page === 1 ? null : page,
            pageSize: pageSize === defaultPageSize ? null : pageSize,
        },
    };
}

export function navigateProfilePaging(
    router: Router,
    route: ActivatedRoute,
    params: Params | ProfilePaging,
    defaultPageSize: number,
    replaceUrl: boolean = false,
    totalCount?: number,
): boolean {
    const { queryParams }: ResolvedProfilePaging = resolveProfilePaging(
        params,
        defaultPageSize,
        totalCount,
    );
    const current: Params = route.snapshot.queryParams;
    if (
        Object.entries(queryParams).every(
            ([key, value]: [string, number | null]): boolean =>
                value === null
                    ? current[key] === undefined
                    : current[key] === value || current[key] === String(value),
        )
    ) {
        return false;
    }
    void router.navigate([], {
        relativeTo: route,
        queryParams,
        queryParamsHandling: 'merge',
        replaceUrl,
    });
    return true;
}
